// What the UI shows for a detected chord (design: music-logic.md). Chords are stored as they
// sound; transpose, capo, spelling and notation are applied here, at display time.
import type { KeyEstimate } from '../analysis/types';
import { mod } from '../util/math';
import type { Chord, ChordQuality } from './chord';
import { QUALITIES } from './chord';
import { keyUsesFlats, noteName } from './key';

export type Notation = 'letters' | 'german' | 'solfege' | 'roman' | 'nashville';
export type Accidentals = 'auto' | 'sharps' | 'flats';

export interface DisplayOptions {
  /** Detected key of the song (sounding). */
  key: { tonic: number; mode: KeyEstimate['mode'] };
  /** Semitones, −11…11 (F5.1). */
  transpose: number;
  /** Capo fret, 0…11 (F5.2). Shapes shift down by this much. */
  capo: number;
  notation: Notation;
  accidentals: Accidentals;
}

export const NO_CHORD_LABEL = 'N.C.';

/** Chords whose confidence is below this are drawn dashed, "less certain" (F2.10). */
export const LOW_CONFIDENCE = 0.6;

/** Spell with flats? Auto follows the key the player reads in: sounding key + transpose − capo. */
export function displayUsesFlats(o: DisplayOptions): boolean {
  if (o.accidentals === 'flats') return true;
  if (o.accidentals === 'sharps') return false;
  return keyUsesFlats(o.key.tonic + o.transpose - o.capo, o.key.mode);
}

const SOLFEGE: Record<string, string> = {
  C: 'Do',
  D: 'Re',
  E: 'Mi',
  F: 'Fa',
  G: 'Sol',
  A: 'La',
  B: 'Si',
};
const ROMAN = ['I', '♭II', 'II', '♭III', 'III', 'IV', '♯IV', 'V', '♭VI', 'VI', '♭VII', 'VII'];
const NASHVILLE = ['1', '♭2', '2', '♭3', '3', '4', '♯4', '5', '♭6', '6', '♭7', '7'];

/** A root in the chosen letter style ("B♭", German "B", solfège "Si♭"). */
function styledRoot(pc: number, flats: boolean, notation: Notation): string {
  const root = noteName(pc, flats);
  if (notation === 'german') {
    if (root === 'B') return 'H';
    if (root === 'B♭' || root === 'A♯') return 'B';
    return root;
  }
  if (notation === 'solfege') return (SOLFEGE[root[0] ?? ''] ?? root) + root.slice(1);
  return root;
}

/** Suffix after a Roman numeral; minor-family chords show as a lower-case numeral instead. */
const ROMAN_SUFFIX: Record<ChordQuality, string> = {
  maj: '',
  min: '',
  '7': '7',
  maj7: 'maj7',
  m7: '7',
  '6': '6',
  add9: 'add9',
  sus2: 'sus2',
  sus4: 'sus4',
  dim: '°',
  aug: '+',
};

/** Suffix after a Nashville number. */
const NASHVILLE_SUFFIX: Record<ChordQuality, string> = {
  ...ROMAN_SUFFIX,
  min: 'm',
  m7: 'm7',
};

/**
 * The label for grid cells, timeline blocks and Now/Next.
 * Letter styles show the shape to play (sounding + transpose − capo). Roman and Nashville
 * numerals are relative to the song key, so transpose and capo don't change them.
 */
export function chordName(chord: Chord | null, o: DisplayOptions): string {
  if (!chord) return NO_CHORD_LABEL;
  if (o.notation === 'roman' || o.notation === 'nashville') {
    const degree = mod(chord.root - o.key.tonic, 12);
    if (o.notation === 'nashville') return NASHVILLE[degree]! + NASHVILLE_SUFFIX[chord.quality];
    const numeral = ROMAN[degree]!;
    const minor = QUALITIES[chord.quality].triad === 'min';
    return (minor ? numeral.toLowerCase() : numeral) + ROMAN_SUFFIX[chord.quality];
  }
  return (
    styledRoot(chord.root + o.transpose - o.capo, displayUsesFlats(o), o.notation) +
    QUALITIES[chord.quality].suffix
  );
}

/** The sounding key for the Key chip, e.g. "E♭ major": transpose applies, capo doesn't. */
export function soundingKeyName(o: DisplayOptions): string {
  const tonic = mod(o.key.tonic + o.transpose, 12);
  return `${noteName(tonic, keyUsesFlats(tonic, o.key.mode))} ${o.key.mode}`;
}

/** Major keys with easy open guitar shapes: C, G, D, A, E. */
const EASY_MAJOR_KEYS = [0, 7, 2, 9, 4];

export interface CapoSuggestion {
  capo: number;
  /** Shape key to play, e.g. "D" or "Am". */
  shapeKey: string;
}

/**
 * Lowest capo (0–7) that makes the sounding key an easy open-shape key (F5.3). For minor keys
 * the shape key's relative major must be easy: C♯ minor (relative E) needs no capo,
 * B♭ minor (relative D♭) takes capo 1 and plays Am shapes.
 */
export function suggestCapo(o: DisplayOptions): CapoSuggestion {
  const sounding = mod(o.key.tonic + o.transpose, 12);
  const relativeMajor = o.key.mode === 'major' ? sounding : mod(sounding + 3, 12);
  for (let capo = 0; capo <= 7; capo++) {
    if (EASY_MAJOR_KEYS.includes(mod(relativeMajor - capo, 12))) {
      const shapeTonic = mod(sounding - capo, 12);
      return { capo, shapeKey: noteName(shapeTonic, false) + (o.key.mode === 'minor' ? 'm' : '') };
    }
  }
  return { capo: 0, shapeKey: noteName(sounding, false) + (o.key.mode === 'minor' ? 'm' : '') };
}

/** "+2", "−3", "0" (real minus sign). */
export function formatTranspose(semitones: number): string {
  if (semitones > 0) return `+${semitones}`;
  if (semitones < 0) return `−${Math.abs(semitones)}`;
  return '0';
}
